import React, { useMemo, useState, useEffect } from "react";
import { Card, CardBody, CardTitle, Input, Button, Table, Badge, Row, Col, CardHeader, Spinner } from "reactstrap";
import { AiOutlineSearch, AiOutlineFileExcel } from "react-icons/ai";
import { FiTrendingUp, FiBarChart2 } from "react-icons/fi";
import * as XLSX from "xlsx";
import axios from "axios";
import "./SubAdminReportManagement.css";

const API_URL = process.env.REACT_APP_API_URL;

const getToken = () => {
  // Try both 'token' and 'authToken' for backward compatibility
  return sessionStorage.getItem("token") || 
         localStorage.getItem("token") || 
         sessionStorage.getItem("authToken") || 
         localStorage.getItem("authToken");
};

/**
 * SubAdmin Report Management (Admin/SuperAdmin view)
 * Displays per-SubAdmin totals and upload success rate, with search/filter and export.
 */

function SubAdminReportManagement() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchAggregatedStats = async () => {
      setLoading(true);
      try {
        const token = getToken();
        if (!token) {
          setData([]);
          setLoading(false);
          return;
        }
        const response = await axios.get(`${API_URL}/admin/sub-admin-aggregated-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data?.data || []);
      } catch (error) {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAggregatedStats();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data
      .map((item) => {
        // Map backend fields to frontend expected structure
        const subAdminName = item.fullName || item.subAdminName || "";
        const agentCode = item.employeeId || item.agentCode || "-";
        const referralAgentCode = item.referralAgentCode || item.referalAgentCode || "-";
        const referralAgentName = item.referralAgentName || item.referalAgentName || "-";
        const uploadsTotal = item.totalRecords ?? item.uploadsTotal ?? 0;
        const uploadsSuccessful = item.validRecords ?? item.uploadsSuccessful ?? 0;
        const successRate = item.validPercentage ?? (uploadsTotal > 0 ? Math.round((uploadsSuccessful / uploadsTotal) * 100) : 0);
        const walletRechargeTotal = item.walletRechargeTotal ?? 0;
        const updatedAt = item.updatedAt || null;
        return {
          ...item,
          subAdminName,
          agentCode,
          referralAgentCode,
          referralAgentName,
          uploadsTotal,
          uploadsSuccessful,
          successRate,
          walletRechargeTotal,
          updatedAt,
        };
      })
      .filter((item) => {
        const matchesSearch = !term || item.subAdminName.toLowerCase().includes(term) || item.agentCode.toLowerCase().includes(term);
        let matchesDate = true;
        if (filterMonth || filterYear) {
          const d = item.updatedAt ? new Date(item.updatedAt) : null;
          if (!d) matchesDate = false;
          if (matchesDate && filterMonth && d.getMonth() + 1 !== Number(filterMonth)) {
            matchesDate = false;
          }
          if (matchesDate && filterYear && d.getFullYear() !== Number(filterYear)) {
            matchesDate = false;
          }
        }
        return matchesSearch && matchesDate;
      });
  }, [data, search, filterMonth, filterYear]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalAmount = filtered.reduce((sum, item) => sum + item.walletRechargeTotal, 0);
    const avgSuccessRate = filtered.length > 0
      ? Math.round(filtered.reduce((sum, item) => sum + item.successRate, 0) / filtered.length)
      : 0;
    const totalUploads = filtered.reduce((sum, item) => sum + item.uploadsTotal, 0);
    const successfulUploads = filtered.reduce((sum, item) => sum + item.uploadsSuccessful, 0);
    return { totalAmount, avgSuccessRate, totalUploads, successfulUploads };
  }, [filtered]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterMonth, filterYear, data.length, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRows = useMemo(() => {
    return filtered.slice(startIndex, startIndex + pageSize);
  }, [filtered, startIndex, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleExport = () => {
    const rows = filtered.map((row) => ({
      "Coordinate Agent Name": row.subAdminName,
      "Agent Code": row.agentCode,
      "Referral Agent Code": row.referralAgentCode,
      "Referral Agent Name": row.referralAgentName,
      "Wallet Recharge Total": row.walletRechargeTotal,
      "Upload Success Rate (%)": row.successRate,
      "Uploads Successful": row.uploadsSuccessful,
      "Uploads Total": row.uploadsTotal,
      "Last Updated": row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Coordinate Agent Report");
    XLSX.writeFile(workbook, "coordinate-agent-report.xlsx");
  };

  return (
    <div className="container-fluid">
      {/* Summary Cards */}
      <Row className="mb-4 g-3">
        <Col md="6">
          <Card className="shadow-sm border-0 bg-gradient sarm-card-success">
            <CardBody className="py-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small fw-semibold">Total Amount Recharged</p>
                  <h3 className="mb-0 text-success fw-bold">
                     {(summaryMetrics.totalAmount ?? 0).toLocaleString()}
                  </h3>
                  <small className="text-muted">All Coordinate Agents</small>
                </div>
                <div className="sarm-icon-success">
                  <FiTrendingUp />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card className="shadow-sm border-0 bg-gradient sarm-card-primary">
            <CardBody className="py-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small fw-semibold">Average Upload Success Rate</p>
                  <h3 className="mb-0 text-primary fw-bold">
                    {summaryMetrics.avgSuccessRate}%
                  </h3>
                  <small className="text-muted">
                    ({summaryMetrics.successfulUploads} / {summaryMetrics.totalUploads} uploads)
                  </small>
                </div>
                <div className="sarm-icon-primary">
                  <FiBarChart2 />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Report Table */}
      <Card className="shadow-sm border-0">
        <CardHeader className="bg-light border-bottom">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <CardTitle tag="h5" className="mb-0">
              <span className="text-dark fw-bold">Reports</span>
            </CardTitle>
            <Button color="success" size="sm" onClick={handleExport} className="d-flex align-items-center gap-2">
              <AiOutlineFileExcel /> Export to Excel
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="g-2 mb-4">
            <Col md="8">
              <div className="sarm-search-wrapper">
                <AiOutlineSearch className="position-absolute sarm-search-icon" />
                <Input
                  placeholder="Search Coordinate Agent or Agent Code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded sarm-search-input"
                />
              </div>
            </Col>
            <Col md="2">
              <Input
                type="select"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="rounded"
              >
                <option value="">Month</option>
                {[...Array(12)].map((_, idx) => (
                  <option key={idx + 1} value={idx + 1}>{idx + 1}</option>
                ))}
              </Input>
            </Col>
            <Col md="2">
              <Input
                type="select"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="rounded"
              >
                <option value="">Year</option>
                {[2024, 2025, 2026].map((yr) => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </Input>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th className="fw-semibold text-dark sarm-table-header">#</th>
                  <th className="fw-semibold text-dark sarm-table-header">Coordinate Agent Name</th>
                  <th className="fw-semibold text-dark sarm-table-header">Agent Code</th>
                  <th className="fw-semibold text-dark sarm-table-header">Referral Agent Code</th>
                  <th className="fw-semibold text-dark sarm-table-header">Referral Agent Name</th>
                  <th className="fw-semibold text-dark sarm-table-header">Total Amount Recharged</th>
                  <th className="fw-semibold text-dark sarm-table-header">Upload Success %</th>
                  <th className="fw-semibold text-dark sarm-table-header">Uploads (Success/Total)</th>
                  <th className="fw-semibold text-dark sarm-table-header">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading Coordinate Agent Reports...</p>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-5">
                      <p className="mb-0">No data found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={row.subAdminName} className="border-bottom">
                      <td className="text-muted fw-semibold">{startIndex + idx + 1}</td>
                      <td className="fw-semibold text-dark">{row.subAdminName}</td>
                      <td className="text-muted">{row.agentCode}</td>
                      <td className="text-muted">{row.referralAgentCode}</td>
                      <td className="text-muted">{row.referralAgentName}</td>
                      <td>
                        <Badge color="primary" className="fs-6 px-3 py-2">
                           {(row.walletRechargeTotal ?? 0).toLocaleString()}
                        </Badge>
                      </td>
                      <td>
                        <Badge 
                          color={row.successRate >= 90 ? "success" : row.successRate >= 70 ? "warning" : "danger"}
                          className="fs-6 px-3 py-2"
                        >
                          {row.successRate}%
                        </Badge>
                      </td>
                      <td className="text-muted">
                        <small>{row.uploadsSuccessful} / {row.uploadsTotal}</small>
                      </td>
                      <td className="text-muted">
                        <small>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "N/A"}</small>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {filtered.length > 0 && (
            <div className="mt-3 pt-3 border-top d-flex flex-wrap align-items-center justify-content-between gap-2 text-muted small">
              <div>
                <strong>Total Records:</strong> {filtered.length} Coordinate Agents
              </div>
              <div className="d-flex align-items-center gap-2">
                <span>Rows:</span>
                <Input
                  type="select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{ width: "80px" }}
                >
                  {[5, 10, 20, 50].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </Input>
                <Button
                  size="sm"
                  color="light"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                >
                  Prev
                </Button>
                <span>Page {currentPage} of {totalPages}</span>
                <Button
                  size="sm"
                  color="light"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default SubAdminReportManagement;

